// src/components/OnboardingModal.jsx
import { useState } from 'react';
import { Building2, Mail, Phone, MapPin, Globe, FileText, Palette, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import MediaUploader from '../../components/UI/MediaUploader.jsx';

const STEPS = [
  { id: 1, title: 'Business Info', subtitle: 'Tell us about your company' },
  { id: 2, title: 'Contact Details', subtitle: 'How clients can reach you' },
  { id: 3, title: 'Branding', subtitle: 'Make the platform yours' },
];

function StepIndicator({ current }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {STEPS.map((step, i) => (
        <div key={step.id} className="flex items-center gap-2">
          <div className="flex flex-col items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${
                current > step.id
                  ? 'bg-[var(--accent-success)] text-white'
                  : current === step.id
                  ? 'bg-[var(--accent-primary)] text-white'
                  : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)]'
              }`}
            >
              {current > step.id ? <CheckCircle className="w-4 h-4" /> : step.id}
            </div>
            <span className={`text-xs mt-1 hidden sm:block ${current === step.id ? 'text-[var(--accent-primary)]' : 'text-[var(--text-muted)]'}`}>
              {step.title}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div className={`h-px w-12 sm:w-16 mb-4 transition-all duration-300 ${current > step.id + 1 ? 'bg-[var(--accent-success)]' : current > step.id ? 'bg-[var(--accent-primary)]' : 'bg-[var(--border-color)]'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

function Field({ label, required, icon: Icon, error, ...inputProps }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5">
        {label} {required && <span className="text-[var(--accent-danger)]">*</span>}
      </label>
      <div className="relative">
        {Icon && (
          <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
        )}
        <input
          {...inputProps}
          className={`w-full ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-2.5 rounded-lg bg-[var(--bg-tertiary)] border ${
            error ? 'border-[var(--accent-danger)]' : 'border-[var(--border-color)]'
          } focus:border-[var(--accent-primary)] focus:outline-none transition-all text-sm`}
        />
      </div>
      {error && <p className="text-xs text-[var(--accent-danger)] mt-1">{error}</p>}
    </div>
  );
}

export default function OnboardingModal({ onComplete }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    name: '',
    legal_name: '',
    license_number: '',
    support_email: '',
    support_phone: '',
    address: '',
    domain: '',
    primary_color: '#1976D2',
    secondary_color: '#424242',
    logo_url: '',
  });

  const set = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    if (errors[field]) setErrors((err) => ({ ...err, [field]: '' }));
  };

  const setField = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((err) => ({ ...err, [field]: '' }));
  };

  const validateStep = () => {
    const newErrors = {};
    if (step === 1) {
      if (!form.name.trim()) newErrors.name = 'Trading name is required';
      if (!form.legal_name.trim()) newErrors.legal_name = 'Legal name is required';
    }
    if (step === 2) {
      if (!form.support_email.trim()) newErrors.support_email = 'Support email is required';
      else if (!/\S+@\S+\.\S+/.test(form.support_email)) newErrors.support_email = 'Invalid email';
      if (!form.support_phone.trim()) newErrors.support_phone = 'Support phone is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const next = () => {
    if (validateStep()) setStep((s) => s + 1);
  };

  const back = () => setStep((s) => s - 1);

  const submit = async () => {
    if (!validateStep()) return;
    setLoading(true);
    try {
      const response = await fetch('/api/broker/onboarding/complete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
        },
        body: JSON.stringify(form),
      });
      const data = await response.json();

      if (data.success) {
        toast.success('Welcome! Your platform is ready.');
        onComplete(data.data);
      } else {
        toast.error(data.error || 'Failed to save. Please try again.');
      }
    } catch (err) {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-[var(--bg-secondary)] rounded-2xl border border-[var(--border-color)] shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="px-8 pt-8 pb-4">
          <div className="text-center mb-2">
            <div className="w-12 h-12 rounded-xl bg-[var(--accent-primary)]/10 flex items-center justify-center mx-auto mb-3">
              <Building2 className="w-6 h-6 text-[var(--accent-primary)]" />
            </div>
            <h2 className="text-xl font-bold">Welcome! Let's set up your platform</h2>
            <p className="text-sm text-[var(--text-secondary)] mt-1">
              Just a few details to get you started — takes under 2 minutes.
            </p>
          </div>
          <StepIndicator current={step} />
        </div>

        {/* Form body */}
        <div className="px-8 pb-6 space-y-4 min-h-[240px]">

          {/* Step 1: Business Info */}
          {step === 1 && (
            <>
              <Field
                label="Trading / Brand Name"
                required
                icon={Building2}
                placeholder="e.g. Acme Markets"
                value={form.name}
                onChange={set('name')}
                error={errors.name}
              />
              <Field
                label="Legal Company Name"
                required
                icon={FileText}
                placeholder="e.g. Acme Markets LLC"
                value={form.legal_name}
                onChange={set('legal_name')}
                error={errors.legal_name}
              />
              <Field
                label="License / Registration Number"
                icon={FileText}
                placeholder="Optional"
                value={form.license_number}
                onChange={set('license_number')}
              />
            </>
          )}

          {/* Step 2: Contact */}
          {step === 2 && (
            <>
              <Field
                label="Support Email"
                required
                type="email"
                icon={Mail}
                placeholder="support@yourcompany.com"
                value={form.support_email}
                onChange={set('support_email')}
                error={errors.support_email}
              />
              <Field
                label="Support Phone"
                required
                type="tel"
                icon={Phone}
                placeholder="+1 234 567 890"
                value={form.support_phone}
                onChange={set('support_phone')}
                error={errors.support_phone}
              />
              <Field
                label="Business Address"
                icon={MapPin}
                placeholder="123 Main St, City, Country (optional)"
                value={form.address}
                onChange={set('address')}
              />
              <Field
                label="Custom Domain"
                icon={Globe}
                placeholder="trading.yourcompany.com (optional, set later)"
                value={form.domain}
                onChange={set('domain')}
              />
            </>
          )}

          {/* Step 3: Branding */}
          {step === 3 && (
            <>
              {/* Logo — MediaUploader */}
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  Logo <span className="text-[var(--text-muted)] font-normal">(optional)</span>
                </label>
                <MediaUploader
                  value={form.logo_url}
                  onChange={(url) => setField('logo_url', url)}
                  accept="image"
                  label="Brand Logo"
                />
                <p className="text-xs text-[var(--text-muted)] mt-1.5">
                  Shown on the login page and client portal. Recommended: square, min 128×128px.
                </p>
              </div>

              {/* Colors */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5 flex items-center gap-2">
                    <Palette className="w-4 h-4 text-[var(--text-muted)]" />
                    Primary Color
                  </label>
                  <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-color)]">
                    <input
                      type="color"
                      value={form.primary_color}
                      onChange={set('primary_color')}
                      className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent"
                    />
                    <span className="text-sm font-mono">{form.primary_color}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5 flex items-center gap-2">
                    <Palette className="w-4 h-4 text-[var(--text-muted)]" />
                    Secondary Color
                  </label>
                  <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-color)]">
                    <input
                      type="color"
                      value={form.secondary_color}
                      onChange={set('secondary_color')}
                      className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent"
                    />
                    <span className="text-sm font-mono">{form.secondary_color}</span>
                  </div>
                </div>
              </div>

              {/* Brand preview */}
              <div className="p-3 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-color)]">
                <p className="text-xs text-[var(--text-muted)] mb-2">Brand preview</p>
                <div className="flex items-center gap-3">
                  {/* Logo preview */}
                  {form.logo_url ? (
                    <img
                      src={form.logo_url}
                      alt="Logo preview"
                      className="w-8 h-8 rounded-lg object-contain border"
                      style={{ borderColor: 'var(--border-color)' }}
                    />
                  ) : (
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: form.primary_color }}
                    >
                      {form.name ? form.name.charAt(0).toUpperCase() : '?'}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <span
                      className="px-3 py-1 rounded-full text-xs text-white font-medium"
                      style={{ backgroundColor: form.primary_color }}
                    >
                      Primary
                    </span>
                    <span
                      className="px-3 py-1 rounded-full text-xs text-white font-medium"
                      style={{ backgroundColor: form.secondary_color }}
                    >
                      Secondary
                    </span>
                  </div>
                </div>
              </div>

              <p className="text-xs text-[var(--text-muted)] text-center pt-1">
                You can change all of this later in Settings.
              </p>
            </>
          )}
        </div>

        {/* Footer navigation */}
        <div className="px-8 pb-8 flex items-center justify-between gap-3 border-t border-[var(--border-color)] pt-5">
          {step > 1 ? (
            <button
              onClick={back}
              disabled={loading}
              className="px-5 py-2.5 rounded-lg border border-[var(--border-color)] text-sm font-medium hover:bg-[var(--bg-tertiary)] transition-all disabled:opacity-50"
            >
              Back
            </button>
          ) : (
            <div />
          )}

          {step < STEPS.length ? (
            <button
              onClick={next}
              className="px-6 py-2.5 rounded-lg bg-[var(--accent-primary)] hover:bg-[var(--accent-primary-hover)] text-white text-sm font-semibold transition-all"
            >
              Continue →
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={loading}
              className="px-6 py-2.5 rounded-lg bg-[var(--accent-primary)] hover:bg-[var(--accent-primary-hover)] text-white text-sm font-semibold transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Complete Setup
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}